// ── Annotations CSV export (pure FE) ───────────────────────────────────────
//
// Extracted from PublishedPageV3/index.tsx to keep the page component light —
// this whole file (pure helpers + the download orchestration hook) lives
// here so the page stays focused on rendering.
//
// Rows come straight off the fields' annoData: verbatim text (from the
// fragment's editorState), gist, Signals/Topics split by subThemeDisplay
// (tagId prefix as fallback for legacy tags), speaker parsed from the
// field allText at the anno's absoluteOffset, and Customer Voice resolved
// against the doc summary's actors (roleForAnnotation).
//
// Async note: Customer Voice needs the doc summary's actors, but the
// summary query is lazily gated (fires when the AI panel opens). A
// download click triggers the fetch and the effect exports once it
// settles — on error we export anyway with blank Customer Voice rather
// than blocking the download.

import { useCallback, useEffect, useState } from "react";
import { extractAllBlocksFromTpl, getVal } from "@okf/ootils";

// Flatten a Lexical editorState fragment to plain text (annoData fragments
// carry no allText — only the editorState tree).
const extractPlainTextFromEditorState = (editorState: any): string => {
  const out: string[] = [];
  const walkNode = (n: any) => {
    if (!n || typeof n !== "object") return;
    if (typeof n.text === "string") out.push(n.text);
    (Array.isArray(n.children) ? n.children : []).forEach(walkNode);
  };
  walkNode(editorState?.root);
  return out.join(" ").replace(/\s+/g, " ").trim();
};

const csvCell = (v: string) => `"${String(v ?? "").replace(/"/g, '""')}"`;

// Nearest speaker label AT or BEFORE the annotation's absolute offset in the
// field's allText. Handles the two diarized shapes we ingest:
//   Fathom-style   "@12:34 - Yashna Jhamb (Ooloi Labs)"
//   plain diarized "Yashna: ..."
// Returns "" when no label precedes the span (non-diarized fields).
const findSpeakerBefore = (allText: string, start: number): string => {
  if (!allText || typeof start !== "number") return "";
  const hay = allText.slice(0, Math.min(start + 80, allText.length));
  let best: { index: number; name: string } | null = null;
  const patterns = [
    /@\d+:\d+(?::\d+)?\s*-\s*([^(\n]+)/g,
    /(?:^|\n)\s*([A-Z][A-Za-z .'-]{1,40}?)\s*:(?!\d)/g,
  ];
  for (const re of patterns) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(hay))) {
      if (m.index > start) break;
      const name = (m[1] || "").trim();
      if (!name || /^\d/.test(name)) continue;
      if (!best || m.index >= best.index) best = { index: m.index, name };
    }
  }
  return best ? best.name : "";
};

type BuildCsvArgs = {
  tpl: any;
  liveContribution: any;
  aiSummary: any;
  annoOrderIndex: Map<string, number>;
};

// Pure builder — takes the inputs it needs, returns a CSV string.
const buildAnnotationsCsv = ({
  tpl,
  liveContribution,
  aiSummary,
  annoOrderIndex,
}: BuildCsvArgs): string => {
  const actors: any[] = Array.isArray(aiSummary?.actors) ? aiSummary.actors : [];
  const actorFor = (speaker: string) => {
    if (!speaker) return null;
    const s = speaker.toLowerCase();
    return (
      actors.find((a: any) => {
        const n = String(a?.name || "").toLowerCase();
        return n && (s.includes(n) || n.includes(s));
      }) ?? null
    );
  };

  const rows: { rank: number; cells: string[] }[] = [];
  extractAllBlocksFromTpl({ tpl }).forEach((block: any) => {
    const value = block?.valuePath && getVal(liveContribution, block.valuePath);
    if (!value?.isLexical || !value?.annoData) return;
    const allText: string = value.allText || "";
    for (const [id, entry] of Object.entries<any>(value.annoData)) {
      if (!entry) continue;
      const text = extractPlainTextFromEditorState(entry.fragment?.editorState);
      const tagItems = Object.values<any>(entry.tags || {}).flatMap(
        (c: any) => (Array.isArray(c?.data) ? c.data : []),
      );
      const signals = tagItems
        .filter((t: any) => t?.subThemeDisplay === "Signals" || String(t?.tagId || "").startsWith("signals"))
        .map((t: any) => t.display);
      const topics = tagItems
        .filter((t: any) => t?.subThemeDisplay === "Topics" || String(t?.tagId || "").startsWith("topics"))
        .map((t: any) => t.display);
      const speaker = findSpeakerBefore(allText, entry.absoluteOffset?.start);
      const actor = actorFor(speaker);
      const customerVoice = actor?.roleForAnnotation
        ? String(actor.roleForAnnotation === "customer")
        : "";
      rows.push({
        rank: annoOrderIndex.has(id) ? annoOrderIndex.get(id)! : Number.MAX_SAFE_INTEGER,
        cells: [text, entry.observation?.desc || "", signals.join("; "), topics.join("; "), speaker, customerVoice],
      });
    }
  });
  rows.sort((a, b) => a.rank - b.rank);

  const header = ["Annotation Text", "Gist", "Signals", "Topics", "Actor Name", "Customer Voice"];
  return [header, ...rows.map((r) => r.cells)]
    .map((cells) => cells.map(csvCell).join(","))
    .join("\n");
};

// Trigger a browser download of the given csv string, filename derived
// from the contribution title.
const downloadCsv = (csv: string, contribution: any) => {
  // BOM so Excel opens it as UTF-8 (gists carry em-dashes/curly quotes).
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  const title =
    String(contribution?.main?.title || "annotations").replace(/[^\w\- ]+/g, "").trim() ||
    "annotations";
  a.download = `${title} - annotations.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

type UseAnnotationsCsvDownloadArgs = {
  tpl: any;
  liveContribution: any;
  aiSummary: any;
  aiSummaryEnabled: boolean;
  documentSummaryStatus: string;
  setSummaryRequested: (v: boolean) => void;
  contribution: any;
  annoOrderIndex: Map<string, number>;
};

/**
 * Orchestrates the annotations-CSV download: on click, exports immediately
 * if the doc summary is already loaded (or disabled); otherwise triggers
 * the summary fetch and exports as soon as it settles (success or error).
 */
export const useAnnotationsCsvDownload = ({
  tpl,
  liveContribution,
  aiSummary,
  aiSummaryEnabled,
  documentSummaryStatus,
  setSummaryRequested,
  contribution,
  annoOrderIndex,
}: UseAnnotationsCsvDownloadArgs) => {
  const doDownload = useCallback(() => {
    const csv = buildAnnotationsCsv({ tpl, liveContribution, aiSummary, annoOrderIndex });
    downloadCsv(csv, contribution);
  }, [tpl, liveContribution, aiSummary, annoOrderIndex, contribution]);

  const [downloadPending, setDownloadPending] = useState(false);

  const handleDownloadAnnotations = useCallback(() => {
    if (aiSummary || !aiSummaryEnabled) {
      doDownload();
      return;
    }
    setDownloadPending(true);
    setSummaryRequested(true);
  }, [aiSummary, aiSummaryEnabled, doDownload, setSummaryRequested]);

  useEffect(() => {
    if (!downloadPending) return;
    if (documentSummaryStatus === "loading" || documentSummaryStatus === "idle") return;
    setDownloadPending(false);
    doDownload();
  }, [downloadPending, documentSummaryStatus, doDownload]);

  return { handleDownloadAnnotations, downloadPending };
};
